import "./DashboardQuiz.scss";

//React Hooks
import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// helper functions
import { produce } from "immer";

/* Components needed for the Quiz */
import QuizProgressBar from "../QuizProgressBar/QuizProgressBar";
import QIOptionCard from "../QuizOptionCard/QIOptionCard";
import QISelectMakeModel from "../QISelectMakeModel/QISelectMakeModel";
import QIBudgetSelector from "../QIBudgetSelector/QIBudgetSelector";
import QICustomerDetailsForm from "../QICustomerDetailsForm/QICustomerDetailsForm";
import QIRanking from "../QIRanking/QIRanking";
import QITestDrive from "../QITestDrive/QITestDrive";
import QICheckboxes from "../QICheckboxes/QICheckboxes";
import ButtonPlain from "../ButtonPlain/ButtonPlain";
import LoadingScreen from "../LoadingScreen/LoadingScreen";
import QuizFactWidget from "../QuizFact/QuizFactWidget";
import VPOGTrimComparison from "../VPOGTrimComparison/VPOGTrimComparison";
import MonthlyBudgetEstimatorWidget from "../MonthlyBudgetEstimatorWidget/MonthlyBudgetEstimatorWidget";

// contexts
import { useDealershipContext } from "../../contexts/dealershipContext";
import { createDealershipUser } from "../../services/dealership-user";
import { useDealershipUserContext } from "../../contexts/dealershipUserContext";

/* SVG needed in the dashboard quiz */
import { ReactComponent as MaeCoreLogoYellow } from "../../assets/logos/Mae-Minimized-Logo-Yellow.svg";

// Constants
import {
  BUDGET_QUIZ_INTERFACE,
  CUSTOMER_FORM_QUIZ_INTERFACE,
  MAKE_MODEL_SELECTION_QUIZ_INTERFACE,
  RANKING_QUIZ_INTERFACE,
  CHECKBOXES_QUIZ_INTERFACE,
  TEST_DRIVE_APPROVAL_QUIZ_INTERFACE,
} from "../../constants/quizInterfaces";
import { questionIdToQuizIndexMapping } from "../../data/dealershipQuiz/quizIndexMapping";

//Quiz Utils
import {
  quizQuestionsFactory,
  getQuizCount,
  getQuizInitialIndex,
  getQuizViewFwdBtnText,
  getQuizViewPrevBtnText,
  prevBtnFunctionSelector,
} from "../../data/dealershipQuiz/quizQuestionsUtils";
import cloneDeep from "lodash.clonedeep";
import { DEALERSHIP_CONFIG } from "../../config/dealershipConfig";
import { useRecordingSessionContext } from "../../contexts/recordingSessionContext";
import { audioStreamingClientWS } from "../../services/mae-audio-streaming-ws";
import {
  IK_IDK_PARENT_QUESTION_ID,
  IK_IDK_QUESTION_ID,
} from "../../constants/questionIds";
import { useUserTrackingContext } from "../../contexts/userTrackingContext";
import {
  AKL_ID,
  AKS_ID,
  ANL_ID,
  ANR_ID,
  ANS_ID,
  MNB_ID,
  MVB_ID,
} from "../../constants/dealershipIds";

export function QuestionInterfaceSelector({
  quizQuestions,
  setQuizQuestions,
  currentQuestionViewIndex,
  childIndex,
  dealershipId,
}) {
  const quizQuestion = quizQuestions[currentQuestionViewIndex];
  const { quizInterface } = quizQuestion
    ? quizQuestion.children[childIndex]
    : {};

  switch (quizInterface) {
    case BUDGET_QUIZ_INTERFACE:
      return (
        <QIBudgetSelector
          currentQuestionViewIndex={currentQuestionViewIndex}
          quizQuestions={quizQuestions}
          setQuizQuestions={setQuizQuestions}
          childIndex={childIndex}
        />
      );

    case MAKE_MODEL_SELECTION_QUIZ_INTERFACE:
      return (
        <QISelectMakeModel
          currentQuestionViewIndex={currentQuestionViewIndex}
          setQuizQuestions={setQuizQuestions}
          quizQuestions={quizQuestions}
          childIndex={childIndex}
        />
      );

    case CUSTOMER_FORM_QUIZ_INTERFACE:
      return (
        <QICustomerDetailsForm
          currentQuestionViewIndex={currentQuestionViewIndex}
          quizQuestions={quizQuestions}
          setQuizQuestions={setQuizQuestions}
          childIndex={childIndex}
          dealershipId={dealershipId}
        />
      );

    case RANKING_QUIZ_INTERFACE:
      return (
        <QIRanking
          currentQuestionViewIndex={currentQuestionViewIndex}
          childIndex={childIndex}
          quizQuestions={quizQuestions}
          setQuizQuestions={setQuizQuestions}
        />
      );

    case CHECKBOXES_QUIZ_INTERFACE:
      return (
        <QICheckboxes
          currentQuestionViewIndex={currentQuestionViewIndex}
          childIndex={childIndex}
          quizQuestions={quizQuestions}
          setQuizQuestions={setQuizQuestions}
        />
      );

    case TEST_DRIVE_APPROVAL_QUIZ_INTERFACE:
      return (
        <QITestDrive
          currentQuestionViewIndex={currentQuestionViewIndex}
          childIndex={childIndex}
          quizQuestions={quizQuestions}
          setQuizQuestions={setQuizQuestions}
        />
      );

    default:
      return (
        <QIOptionCard
          quizQuestions={quizQuestions}
          setQuizQuestions={setQuizQuestions}
          currentQuestionViewIndex={currentQuestionViewIndex}
          childIndex={childIndex}
        />
      );
  }
}

function DashboardQuiz() {
  const allowedStores = [
    ANL_ID,
    ANS_ID,
    ANR_ID,
    AKL_ID,
    AKS_ID,
    MVB_ID,
    MNB_ID,
  ];

  const {
    dealershipId,
    userSource,
    clearDealershipUserSource,
    salesPersonInfo,
    clearSalesPersonInfo,
    baseURL,
  } = useDealershipContext();

  const {
    user,
    quizIndexMapping,
    userId,
    openedThroughEmail,
    requestPending,
    responseError,
  } = useDealershipUserContext();

  const { recordingEnabled, recorderActive } = useRecordingSessionContext();

  const { trackingId } = useUserTrackingContext();

  const navigate = useNavigate();
  const location = useLocation();

  const quizComponentRef = useRef();

  const [minimumSelected, setMinimumSelected] = useState(false);
  const [width, setWidth] = useState(window.innerWidth);

  const [quizQuestions, setQuizQuestions] = useState([]);
  const count = getQuizCount(baseURL, dealershipId, recordingEnabled);
  const [currentQuestionViewIndex, setCurrentQuestionViewIndex] = useState(() =>
    getQuizInitialIndex()
  );
  const [quizSubmitRequestPending, setQuizSubmitRequestPending] =
    useState(false);
  const isLastQuestion = currentQuestionViewIndex + 1 === count;
  const matchesPageURL = userId
    ? `${baseURL}matches?id=${userId}&email=${openedThroughEmail}&audio=${recordingEnabled}`
    : `${baseURL}?audio=${recordingEnabled}`;
  const homePageURL = `${baseURL}?audio=${recordingEnabled}`;

  const disableFunction = (disable = null, quizQuestions) => {
    const disabledQuestionViewFn =
      quizQuestions[currentQuestionViewIndex]?.disable;
    if (typeof disabledQuestionViewFn === "function") {
      const disabled =
        disable !== null
          ? disabledQuestionViewFn.call(quizQuestions, disable)
          : disabledQuestionViewFn.call(quizQuestions);
      setQuizQuestions(
        produce((quizQuestionsDraft) => {
          quizQuestionsDraft[currentQuestionViewIndex].disabled = disabled;
        })
      );
      return disabled;
    }
    return null;
  };

  const [disabledQuestionView, setDisabledQuestionView] = useState(false);

  const [history, setHistory] = useState([]);

  const enableQuestion = () => {
    const disabledQuestionViewTemp = disableFunction(false, quizQuestions);
    setDisabledQuestionView(disabledQuestionViewTemp);
  };

  const disableQuestion = () => {
    const disabledQuestionViewTemp = disableFunction(true, quizQuestions);
    setDisabledQuestionView(disabledQuestionViewTemp);
  };

  const countSelected = () => {
    const currentQuestionView = quizQuestions[currentQuestionViewIndex];
    const questionViewAnswered = currentQuestionView?.isAnswered.call(
      quizQuestions,
      currentQuestionViewIndex
    );
    setMinimumSelected(questionViewAnswered);
    if (
      currentQuestionView?.automaticForward &&
      !currentQuestionView?.visitedOnce &&
      questionViewAnswered
    ) {
      nextQuestion();
    }
  };

  useEffect(() => {
    countSelected();
  }, [quizQuestions, currentQuestionViewIndex, disabledQuestionView]);

  useEffect(() => {
    if (!requestPending) {
      const questions = quizQuestionsFactory(
        baseURL,
        dealershipId,
        user,
        quizIndexMapping,
        recordingEnabled
      );
      setQuizQuestions(questions);
      setCurrentQuestionViewIndex(() => getQuizInitialIndex());
      setDisabledQuestionView(() => disableFunction(null, questions));
    }
  }, [userId, requestPending]);

  useEffect(() => {
    if (quizComponentRef.current) {
      quizComponentRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [currentQuestionViewIndex]);

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const displayInputFieldErrors = () => {
    let firstChildQuestionWithErrorIndx = Infinity;
    quizQuestions[currentQuestionViewIndex]?.children?.forEach(
      (childQuestion, index) => {
        const inputError = childQuestion?.checkInputError?.call(
          quizQuestions,
          childQuestion.inputValue
        );
        if (
          inputError &&
          index < firstChildQuestionWithErrorIndx &&
          childQuestion.inputCondition?.call(quizQuestions) === true
        ) {
          firstChildQuestionWithErrorIndx = index;
        }
        if (
          inputError !== childQuestion.inputError &&
          childQuestion.inputCondition?.call(quizQuestions) === true
        ) {
          setQuizQuestions(
            produce((quizQuestionsDraft) => {
              const currentQuestionDraft =
                quizQuestionsDraft[currentQuestionViewIndex].children[index];
              currentQuestionDraft.inputError = inputError;
            })
          );
        }
      }
    );
    if (firstChildQuestionWithErrorIndx != Infinity) {
      const firstQuestionWithErroreEl = document.getElementById(
        `${firstChildQuestionWithErrorIndx}-${currentQuestionViewIndex}-chilquestion-id`
      );
      firstQuestionWithErroreEl?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const nextQuestion = async () => {
    setQuizQuestions(
      produce((quizQuestionsDraft) => {
        quizQuestionsDraft[currentQuestionViewIndex].visitedOnce = true;
      })
    );
    if (!isLastQuestion) {
      for (
        let nextQuestionIndex = currentQuestionViewIndex + 1;
        nextQuestionIndex < count;
        nextQuestionIndex++
      ) {
        if (
          quizQuestions[nextQuestionIndex].visibilityCondition.call(
            quizQuestions
          ) === true
        ) {
          setHistory((prev) => [...prev, currentQuestionViewIndex]);
          setCurrentQuestionViewIndex(nextQuestionIndex);
          return;
        }
      }
    } else if (quizQuestions[currentQuestionViewIndex].submit) {
      setQuizSubmitRequestPending(true);
      await quizQuestions[currentQuestionViewIndex].submit.call(
        quizQuestions[currentQuestionViewIndex],
        userId,
        dealershipId,
        quizQuestions,
        setQuizQuestions
      );
      setQuizSubmitRequestPending(false);
      navigate(
        DEALERSHIP_CONFIG[dealershipId].redirectAfterTdAndPreApprovalSubmit(
          dealershipId,
          userId,
          0,
          recordingEnabled,
          baseURL
        )
      );
    } else {
      await submit();
    }
  };

  const prevQuestion = async () => {
    if (history.length > 0) {
      setHistory((prev) => {
        const updatedHistory = [...prev];
        const lastIndex = updatedHistory.pop(); // Remove last question
        setCurrentQuestionViewIndex(lastIndex); // Go to the previous question
        return updatedHistory;
      });
    } else if (location.pathname === `${baseURL}matchmaker-quiz`) {
      navigate(`${baseURL}?audio=${recordingEnabled}`);
    } else {
      navigate(matchesPageURL);
    }
  };

  const submit = async () => {
    setQuizSubmitRequestPending(true);
    const quizSelections = [];
    const quizIndexMapping = {};
    for (let i = 0; i < count; i++) {
      const responses = quizQuestions[i].responses.call(quizQuestions[i]);
      if (responses.length && responses.length > 0) {
        responses.forEach((responseObj, _) => {
          quizSelections.push(responseObj);
          const questionIndexName =
            questionIdToQuizIndexMapping[responseObj.questionId];
          quizIndexMapping[questionIndexName] = quizSelections.length - 1;
        });
      } else {
        quizSelections.push(responses);
        const questionIndexName =
          questionIdToQuizIndexMapping[responses.questionId];
        quizIndexMapping[questionIndexName] = quizSelections.length - 1;
      }
    }

    //
    const response = await createDealershipUser(
      quizSelections,
      dealershipId,
      userSource,
      quizIndexMapping,
      salesPersonInfo.name, // sale person name
      salesPersonInfo.email, //sale person email
      salesPersonInfo.oid,
      (recordingEnabled === "2" ||
        recordingEnabled === "1" ||
        recordingEnabled === "3") &&
        recorderActive.current === true
        ? localStorage.getItem("recordingSessionId") || null
        : null,
      trackingId
    );

    clearDealershipUserSource();
    clearSalesPersonInfo();

    audioStreamingClientWS.destroyInstance(
      localStorage.getItem("recordingSessionId") || null,
      "Completed"
    );

    setQuizSubmitRequestPending(false);
    if (!response || !response?.userId) {
      window.location.reload();
    } else {
      navigate(
        DEALERSHIP_CONFIG[dealershipId].redirectAfterCheckInQuizSubmit(
          dealershipId,
          response?.userId,
          0,
          recordingEnabled,
          baseURL
        )
      );
    }
  };

  if (
    requestPending ||
    quizSubmitRequestPending ||
    (!requestPending && responseError)
  ) {
    return <LoadingScreen type="spinner" />;
  }

  if (!dealershipId || !Object.keys(DEALERSHIP_CONFIG).includes(dealershipId)) {
    return navigate(`${baseURL}?audio=${recordingEnabled}`);
  }

  return (
    <div className="quiz" ref={quizComponentRef}>
      <div className="quiz__filler">
        <div className="quiz__intro">
          <div className="quiz__intro--titles">
            <h2 className="header">
              {quizQuestions[currentQuestionViewIndex]?.header
                ? quizQuestions[currentQuestionViewIndex]?.header
                : "Create Your Profile"}
            </h2>
            <QuizProgressBar count={count} active={currentQuestionViewIndex} />
            <h1 className="subheader">
              {quizQuestions[currentQuestionViewIndex]?.viewText}
            </h1>
            <h3 className="subtext">
              {quizQuestions[currentQuestionViewIndex]?.viewSubtext}
            </h3>
          </div>
        </div>
        <div
          className={
            quizQuestions[currentQuestionViewIndex]?.quizInfoPanel
              ? "question_divider_row"
              : "question_divider_column"
          }
        >
          <div className="combined-question-container">
            {quizQuestions[currentQuestionViewIndex]?.children.map(
              (questionObj, childIndex) => {
                //introucing visibility condition for child questions
                if (
                  questionObj?.visibilityCondition &&
                  !questionObj?.visibilityCondition.call(quizQuestions)
                ) {
                  return null;
                }
                return (
                  <div
                    key={childIndex}
                    id={`${childIndex}-${currentQuestionViewIndex}-chilquestion-id`}
                    className="combined-question-subcontainer"
                  >
                    <p
                      className={`combined-option-title ${questionObj?.text && questionObj?.required ? "required-field" : ""}`}
                    >
                      {questionObj.text}
                    </p>
                    <p className="combined-option-subtext">
                      {questionObj?.subtext}
                    </p>
                    <div className={questionObj?.optionCardsClassname}>
                      <QuestionInterfaceSelector
                        quizQuestions={quizQuestions}
                        setQuizQuestions={setQuizQuestions}
                        currentQuestionViewIndex={currentQuestionViewIndex}
                        childIndex={childIndex}
                        dealershipId={dealershipId}
                      />
                    </div>
                  </div>
                );
              }
            )}
          </div>
          {quizQuestions[currentQuestionViewIndex]?.quizInfoPanel?.type ===
            "quizFactWidget" && (
            <QuizFactWidget
              headText={
                quizQuestions[currentQuestionViewIndex].quizInfoPanel?.headText
              }
              infoText={
                quizQuestions[currentQuestionViewIndex].quizInfoPanel?.infoText
              }
              infoImg={
                quizQuestions[currentQuestionViewIndex].quizInfoPanel?.img
              }
              backgroundColor={
                quizQuestions[currentQuestionViewIndex].quizInfoPanel
                  ?.backgroundColor
              }
            />
          )}

          {/* {quizQuestions[currentQuestionViewIndex]?.quizInfoPanel?.type === "monthlyBudgetEstimatorWidget" && 
        quizQuestions[currentQuestionViewIndex]?.quizInfoPanel?.visibilityCondition?.call(quizQuestions) === true && (
          <MonthlyBudgetEstimatorWidget 
          downPayment={quizQuestions[currentQuestionViewIndex]?.quizInfoPanel?.downPayment?.call(quizQuestions)}
          carPrice={quizQuestions[currentQuestionViewIndex]?.quizInfoPanel?.carPrice?.call(quizQuestions)}
          monthlyBudget={quizQuestions[currentQuestionViewIndex]?.quizInfoPanel?.monthlyBudget?.call(quizQuestions)}
        />
        )} */}

          {/* Dialog for comparing trims */}
          {allowedStores.includes(dealershipId) &&
            quizQuestions[currentQuestionViewIndex]?.questionViewId ===
              IK_IDK_PARENT_QUESTION_ID &&
            quizQuestions[currentQuestionViewIndex]?.children?.some(
              (question) =>
                question.childQuestionId === IK_IDK_QUESTION_ID &&
                question.inputValue &&
                question.inputFromInventory
            ) && (
              <VPOGTrimComparison
                quizQuestions={quizQuestions}
                currentQuestionViewIndex={currentQuestionViewIndex}
                dealershipId={dealershipId}
              />
            )}
        </div>
        <div className="quiz__footer">
          <div className="quiz__buttons">
            <div>
              <ButtonPlain
                border="none"
                padding="1.25rem 2.5rem"
                text={getQuizViewPrevBtnText(
                  baseURL,
                  disabledQuestionView,
                  quizQuestions[currentQuestionViewIndex]
                )}
                onClick={
                  prevBtnFunctionSelector(
                    baseURL,
                    disabledQuestionView,
                    quizQuestions[currentQuestionViewIndex]
                  )
                    ? prevQuestion
                    : disableQuestion
                }
              />
            </div>
            <div>
              {
                <ButtonPlain
                  color={minimumSelected ? "#FFE6A9" : "#EEEEED"}
                  border="none"
                  padding={
                    isLastQuestion ? "1.25rem 2.125rem" : "1.25rem 4.125rem"
                  }
                  text={getQuizViewFwdBtnText(
                    baseURL,
                    isLastQuestion,
                    disabledQuestionView
                  )}
                  onClick={
                    disabledQuestionView
                      ? enableQuestion
                      : minimumSelected
                        ? nextQuestion
                        : displayInputFieldErrors
                  }
                />
              }
            </div>
          </div>
          <div className="badge__logo">
            <div style={{ fontWeight: "400", marginRight: "0.75rem" }}>
              Powered By{" "}
            </div>
            <MaeCoreLogoYellow />
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardQuiz;
